import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CustomDatePicker from '../components/CustomDatePicker';
import CustomFileUpload from '../components/CustomFileUpload';
import { isMultiRowOptions, isImageOptions } from '../types/guards';
import { MultiRowField } from '../components/MultiRowField';
import { HeaderField } from '../components/HeaderField';
import AVCheckField from '../components/form-fields/AVCheckField';
import ConnectionCheckField from '../components/form-fields/ConnectionCheckField';
import SchedulerField from '../components/form-fields/SchedulerField';
import { shouldShowSchedulerField } from '../utils/conditionalLogic';
import { prepareUploadFile, MAX_UPLOAD_BYTES } from '../utils/fileCompression';
import { formsApi } from '../api/api';
import { Form, SchedulerOptions } from '../types/form';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useErrorState } from '../hooks/useErrorState';
import { ThemeToggle } from '../components/ThemeToggle';
import { CheckCircle, AlertCircle, Upload, X, File as FileIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LogicFieldProps {
  field: any;
  formData: { [key: string]: any };
  formFields: any[];
}

const LogicField: React.FC<LogicFieldProps> = ({ field, formData, formFields }) => {
  const [calculatedValue, setCalculatedValue] = useState('0');

  useEffect(() => {
    const calculateLogicValue = () => {
      let sourceFieldId = field.options?.sourceFieldId;
      
      if (!sourceFieldId || !formFields.find(f => f.id === sourceFieldId)) {
        const sourceFieldLabel = field.options?.sourceFieldLabel;
        if (sourceFieldLabel) {
          const sourceField = formFields.find(f => f.label === sourceFieldLabel);
          if (sourceField) {
            sourceFieldId = sourceField.id;
          }
        }
      }
      
      if (sourceFieldId) {
        const sourceFieldValue = formData[sourceFieldId];
        if (sourceFieldValue && typeof sourceFieldValue === 'object') {
          
          if (field.options?.logicType === 'sum_checkbox_inputs') {
            let sum = 0;
            Object.entries(sourceFieldValue).forEach(([, option]: [string, any]) => {
              if (option?.checked && option?.value) {
                const numValue = parseFloat(option.value);
                if (!isNaN(numValue)) {
                  sum += numValue;
                }
              }
            });
            return sum.toString();
          }
          
          if (field.options?.logicType === 'exam_duration_calculation') {
            let totalStudents = 0;
            Object.entries(sourceFieldValue).forEach(([, option]: [string, any]) => {
              if (option?.checked && option?.value) {
                const numValue = parseFloat(option.value);
                if (!isNaN(numValue)) {
                  totalStudents += numValue;
                }
              }
            });
            
            if (totalStudents === 0) {
              return 'No students entered';
            }
            
            const studentsPerHall = 12;
            const maxSessionsPerDay = 4;
            const hoursPerDay = (2 * 1) + (2 * 1.5);
            
            const formatDuration = (sessions: number) => {
              const fullDays = Math.floor(sessions / maxSessionsPerDay);
              const remainingSessions = sessions % maxSessionsPerDay;
              
              let totalHours = fullDays * hoursPerDay;
              
              if (remainingSessions > 0) {
                if (remainingSessions <= 2) {
                  totalHours += remainingSessions * 1;
                } else {
                  totalHours += (2 * 1) + ((remainingSessions - 2) * 1.5);
                }
              }
              
              const days = Math.floor(totalHours / hoursPerDay);
              const remainingHours = totalHours % hoursPerDay;
              
              if (days === 0) {
                return `${totalHours} hour(s)`;
              } else if (remainingHours === 0) {
                return `${days} day(s)`;
              } else {
                const hoursPart = Math.floor(remainingHours);
                const minutesPart = Math.round((remainingHours % 1) * 60);
                
                if (minutesPart === 0) {
                  return `${days} day(s) and ${hoursPart} hour(s)`;
                } else {
                  return `${days} day(s) and ${hoursPart} hour(s) ${minutesPart} min(s)`;
                }
              }
            };
            
            const hallsCapacity4 = 4 * studentsPerHall;
            const sessionsNeeded4 = Math.ceil(totalStudents / hallsCapacity4);
            const duration4 = formatDuration(sessionsNeeded4);
            
            // Calculate for 5 halls
            const hallsCapacity5 = 5 * studentsPerHall; // 60 students per session
            const sessionsNeeded5 = Math.ceil(totalStudents / hallsCapacity5);
            const duration5 = formatDuration(sessionsNeeded5);
            
            return `${totalStudents} students → 4 halls: ${duration4}, 5 halls: ${duration5}`;
          }
        }
      }
      return '0';
    };

    setCalculatedValue(calculateLogicValue());
  }, [field, formData, formFields]);

  return (
    <div className="bg-muted/20 border border-muted-foreground/30 rounded-md p-4">
      <div className="text-sm text-muted-foreground mb-2">
        {field.options?.logicType === 'sum_checkbox_inputs' && 'Total Sum'}
        {field.options?.logicType === 'exam_duration_calculation' && 'Exam Duration Calculation'}
        {!field.options?.logicType && 'Calculated Value'}
      </div>
      <div className={`${field.options?.logicType === 'exam_duration_calculation' ? 'text-sm' : 'text-2xl'} font-mono font-bold`}>
        {calculatedValue}
      </div>
    </div>
  );
};

const PublicForm: React.FC = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const { t } = useTranslation();
  const [form, setForm] = useState<Form | null>(null);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [preSelectedFields, setPreSelectedFields] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { handleApiError } = useErrorHandler();
  const { setError: setFormError } = useErrorState();
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchForm = useCallback(async () => {
    if (!identifier) {
      setLoading(false);
      return;
    }

    try {
      const response = await formsApi.getPublicForm(identifier);
      
      setForm(response.data);
      
      const preSelected = response.data.pre_selected_fields || {};
      setPreSelectedFields(preSelected);
      
      setFormData(preSelected);
      
      setAccessDenied(false);
    } catch (error: any) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setFormError(handleApiError(error as any, 'load form', { showNotification: false }));
      }
    } finally {
      setLoading(false);
    }
  }, [identifier, handleApiError, setFormError]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));

    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // File fields: downscale oversized images before storing so the eventual
  // multipart upload is small enough to complete on mobile connections, and
  // reject anything still over the limit up-front (clear message instead of a
  // silent failure at submit time).
  const handleFileFieldChange = async (fieldId: string, file: File | null) => {
    if (!file) {
      handleInputChange(fieldId, file);
      return;
    }
    const prepared = await prepareUploadFile(file);
    if (prepared.size > MAX_UPLOAD_BYTES) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: 'This file is too large (max 10MB). Please upload a smaller file, or a PDF instead of a photo.',
      }));
      return;
    }
    handleInputChange(fieldId, prepared);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    form?.fields.forEach(field => {
      if (field.field_type === 'header' || field.field_type === 'logic' || field.field_type === 'image') {
        return;
      }
      
      if (field.field_type === 'multi_row') {
        const columns = (field.options && isMultiRowOptions(field.options)) ? field.options.columns || [] : [];
        const rows = formData[field.id] || [];
        
        if (field.is_required && rows.length === 0) {
          newErrors[field.id] = `${field.label} is required`;
        }
        
        rows.forEach((row: any[], rowIndex: number) => {
          columns.forEach((column: any, colIndex: number) => {
            if (column.required && (!row[colIndex] || row[colIndex] === '')) {
              newErrors[`${field.id}-${rowIndex}-${colIndex}`] = 
                `${column.label} is required in row ${rowIndex + 1}`;
            }
          });
        });
      } else if (field.field_type === 'checkbox_with_input') {
        const fieldValue = formData[field.id];
        if (field.is_required) {
          const hasCheckedOption = fieldValue && Object.values(fieldValue).some((option: any) => option?.checked);
          if (!hasCheckedOption) {
            newErrors[field.id] = `${field.label} requires at least one option to be checked`;
          } else {
            const checkedOptionsWithEmptyInput = fieldValue && Object.entries(fieldValue).filter(([, option]: [string, any]) => 
              option?.checked && (!option?.value || option.value.trim() === '')
            );
            if (checkedOptionsWithEmptyInput && checkedOptionsWithEmptyInput.length > 0) {
              newErrors[field.id] = `${field.label} requires input for all checked options`;
            }
          }
        }
      } else if (field.field_type === 'av_check' || field.field_type === 'connection_check') {
        // A required readiness check is only valid once it has passed.
        if (field.is_required && formData[field.id]?.status !== 'pass') {
          newErrors[field.id] = `${field.label} must pass before submitting`;
        }
      } else if (field.field_type === 'scheduler') {
        // Scheduler completion is gated, not a checkbox; nothing to validate here.
        return;
      } else if (field.is_required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm();
  };

  const submitForm = async (): Promise<boolean> => {
    if (submitting) return false;

    if (!validateForm() || !identifier) {
      return false;
    }

    try {
      setSubmitting(true);
      setErrors({}); // Clear any previous errors

      // Check if formData contains any File objects
      const hasFiles = Object.values(formData).some(value => value instanceof File);

      if (hasFiles) {
        // Use FormData for file uploads
        const formDataToSend = new FormData();

        // Build a values object for non-file fields
        const valuesObject: { [key: string]: any } = {};

        // Add all field values to FormData
        form?.fields.forEach(field => {
          const value = formData[field.id];
          if (value !== undefined && value !== null) {
            if (value instanceof File) {
              // Add files directly to FormData root level
              formDataToSend.append(field.id, value as Blob);
            } else if (typeof value === 'object') {
              // For complex objects (like checkbox_with_input, multi_row), add to values object
              valuesObject[field.id] = value;
            } else {
              // For simple values (string, number, etc.), add to values object
              valuesObject[field.id] = value;
            }
          }
        });

        // Add the values object as a JSON string
        formDataToSend.append('values', JSON.stringify(valuesObject));

        await formsApi.submitPublicForm(identifier, formDataToSend);
      } else {
        // Use regular JSON for non-file submissions
        await formsApi.submitPublicForm(identifier, { values: formData });
      }

      setSubmitted(true);
      return true;
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 400 && error.response?.data) {
        const errorData = error.response.data;
        const newErrors: { [key: string]: string } = {};
        
        // Handle different types of error responses
        if (typeof errorData === 'string') {
          // Single error message
          newErrors.general = errorData;
        } else if (errorData.values && typeof errorData.values === 'string') {
          // Error in values field
          newErrors.general = errorData.values;
        } else if (errorData.values && typeof errorData.values === 'object') {
          // Field-specific errors in values object
          Object.keys(errorData.values).forEach(key => {
            const errorValue = errorData.values[key];
            if (Array.isArray(errorValue)) {
              newErrors[key] = errorValue.join(', ');
            } else {
              newErrors[key] = errorValue;
            }
          });
        } else if (errorData.non_field_errors) {
          // Non-field errors
          newErrors.general = Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors.join(', ')
            : errorData.non_field_errors;
        } else {
          // Field-specific errors (including multi-row cell errors)
          Object.keys(errorData).forEach(key => {
            const errorValue = errorData[key];
            if (Array.isArray(errorValue)) {
              // Clean up array format errors and join them
              const cleanedErrors = errorValue.map(err => {
                // Remove array brackets if the error is a stringified array
                if (typeof err === 'string' && err.startsWith('[') && err.endsWith(']')) {
                  try {
                    const parsed = JSON.parse(err);
                    return Array.isArray(parsed) ? parsed.join(', ') : err;
                  } catch (e) {
                    return err.replace(/^\['|'\]$/g, '').replace(/'/g, '');
                  }
                }
                return err;
              });
              newErrors[key] = cleanedErrors.join(', ');
            } else if (typeof errorValue === 'string' && errorValue.startsWith('[') && errorValue.endsWith(']')) {
              // Handle stringified arrays
              try {
                const parsed = JSON.parse(errorValue);
                newErrors[key] = Array.isArray(parsed) ? parsed.join(', ') : errorValue;
              } catch (e) {
                newErrors[key] = errorValue.replace(/^\['|'\]$/g, '').replace(/'/g, '');
              }
            } else {
              newErrors[key] = errorValue;
            }
          });
        }
        
        console.error('Setting errors:', newErrors);
        setErrors(newErrors);
      } else if (!error.response) {
        // No response at all — almost always a dropped/slow mobile connection
        // while uploading files. Reassure them their booking isn't lost.
        setErrors({
          general:
            "Your submission didn't go through — this is usually a weak connection while uploading your files. Your booking is saved. Please check your connection and tap Submit to try again.",
        });
      } else if (error.response.status === 413) {
        setErrors({
          general:
            'Your files are too large to upload. Please upload smaller files (under 10MB each, or a PDF instead of a photo) and submit again.',
        });
      } else if (error.response.status >= 500) {
        setErrors({
          general:
            'Something went wrong on our side. Your booking is saved — please tap Submit to try again in a moment.',
        });
      } else {
        // Generic error handling
        console.error('Using generic error message');
        setErrors({ general: 'An error occurred while submitting the form. Please try again.' });
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit-on-booking ──────────────────────────────────────────────
  // When a scheduler field opts in, the manual submit button is hidden and the
  // form auto-submits as soon as that scheduler reports a completed booking.
  const autoSubmitSchedulerId = useMemo(() => {
    const f = (form?.fields || []).find(
      (fld) => fld.field_type === 'scheduler' && (fld.options as SchedulerOptions)?.submitOnBooking,
    );
    return f?.id;
  }, [form?.fields]);
  // Once an auto-submit attempt fails (validation OR network/server error), fall
  // back to showing the submit button so the respondent isn't stranded after
  // booking with no way to retry.
  const [autoSubmitFailed, setAutoSubmitFailed] = useState(false);
  const hideSubmitButton = !!autoSubmitSchedulerId && !autoSubmitFailed;

  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!autoSubmitSchedulerId) return;
    const booked = !!formData[autoSubmitSchedulerId]?.booked;
    if (booked && !autoSubmittedRef.current && !submitting && !submitted && !autoSubmitFailed) {
      autoSubmittedRef.current = true;
      submitForm().then((ok) => {
        if (!ok) {
          // Surface the manual submit button so the respondent can retry; the
          // autoSubmitFailed gate prevents the effect from auto-retrying in a loop.
          setAutoSubmitFailed(true);
        }
      });
    }
    // submitForm reads live state via closure; the inputs that should re-trigger
    // are in the dep list. One-shot guarded by autoSubmittedRef + autoSubmitFailed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitSchedulerId, formData, submitting, submitted, autoSubmitFailed]);

  // ── Cal.com prefill ────────────────────────────────────────────────
  // Derive the respondent's name + email from their earlier answers so the
  // booking widget is pre-filled (no double entry). Email = first email-type
  // field; name = first+last name fields if present, else a "name" text field.
  const schedulerPrefill = useMemo(() => {
    const fields = form?.fields || [];
    const val = (id?: string) => (id ? formData[id] : undefined);

    const emailField = fields.find((f) => f.field_type === 'email' && val(f.id));
    const email = emailField ? String(val(emailField.id) || '').trim() : undefined;

    const byLabel = (re: RegExp) =>
      fields.find((f) => f.field_type === 'text' && re.test(f.label || '') && val(f.id));
    const first = byLabel(/first\s*name/i);
    const last = byLabel(/last\s*name|surname/i);
    let name: string | undefined;
    if (first || last) {
      name =
        [first && val(first.id), last && val(last.id)]
          .filter(Boolean)
          .map((s) => String(s).trim())
          .join(' ')
          .trim() || undefined;
    } else {
      const full = byLabel(/name/i);
      name = full ? String(val(full.id) || '').trim() || undefined : undefined;
    }
    return { name, email };
  }, [form?.fields, formData]);

  const isFieldPreSelected = (fieldId: string) => {
    return fieldId in preSelectedFields;
  };

  const renderAboveTitleImages = () => {
    if (!form) return null;
    
    const aboveTitleImages = form.fields.filter(field => 
      field.field_type === 'image' && 
      field.options && 
      isImageOptions(field.options) && 
      field.options.aboveTitle === true
    );
    
    if (aboveTitleImages.length === 0) return null;
    
    return (
      <div className="mb-8 space-y-4">
        {aboveTitleImages.map((field) => renderImageField(field))}
      </div>
    );
  };

  const renderImageField = (field: any) => {
    const getImageSizeClass = (size: string) => {
      switch (size) {
        case 'small': return 'max-w-[150px]';
        case 'medium': return 'max-w-[300px]';
        case 'large': return 'max-w-[500px]';
        case 'full': return 'w-full';
        default: return 'max-w-[300px]';
      }
    };
    
    const getImageMaxHeight = (size: string) => {
      switch (size) {
        case 'small': return '150px';
        case 'medium': return '300px';
        case 'large': return '400px';
        case 'full': return '500px';
        default: return '400px';
      }
    };

    // Side-by-side layout (two images)
    if (field.options?.layout === 'side-by-side') {
      return (
        <div key={field.id} className="flex justify-between items-start w-full">
          {(field.options?.imageUrl || field.options?.imageFile) && (
            <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-start`}>
              <img
                src={field.options.imageFile || field.options.imageUrl}
                alt={field.options?.imageAlt || 'Left image'}
                className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />
            </div>
          )}
          {(field.options?.imageUrl2 || field.options?.imageFile2) && (
            <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-end`}>
              <img
                src={field.options.imageFile2 || field.options.imageUrl2}
                alt={field.options?.imageAlt2 || 'Right image'}
                className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Single image layout
    return (field.options?.imageUrl || field.options?.imageFile) ? (
      <div key={field.id} className={`${getImageSizeClass(field.options?.imageSize || 'medium')} ${field.options?.aboveTitle ? '' : 'mx-auto'}`}>
        <img
          src={field.options.imageFile || field.options.imageUrl}
          alt={field.options?.imageAlt || 'Form image'}
          className={`${field.options?.aboveTitle ? 'h-auto' : 'w-full h-auto'} rounded ${field.options?.showBorder !== false ? 'border' : ''}`}
          style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            img.style.display = 'none';
          }}
        />
      </div>
    ) : null;
  };

  const renderField = (field: any) => {
    const value = formData[field.id] || '';
    const error = errors[field.id];
    const isPreSelected = isFieldPreSelected(field.id);
    
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div>
            <Input
              type={field.field_type === 'email' ? 'email' : 'text'}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`${error ? 'border-destructive' : ''} ${isPreSelected ? 'bg-muted/50' : ''}`}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'number':
        return (
          <div>
            <Input
              type="text"
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^0-9.-]/g, '');
                handleInputChange(field.id, numericValue);
              }}
              className={`${error ? 'border-destructive' : ''} ${isPreSelected ? 'bg-muted/50' : ''}`}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'textarea':
        return (
          <div>
            <textarea
              className={`w-full px-3 py-2 border border-input bg-background rounded-md text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                error ? 'border-destructive' : ''
              } ${isPreSelected ? 'bg-muted/50' : ''}`}
              rows={4}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'dropdown':
        return (
          <div>
            <Select value={value} onValueChange={(selectedValue) => handleInputChange(field.id, selectedValue)} disabled={isPreSelected}>
              <SelectTrigger 
                className={`${error ? 'border-destructive' : ''} ${isPreSelected ? 'bg-muted/50' : ''}`}
                aria-describedby={error ? `${field.id}-error` : undefined}
              >
                <SelectValue placeholder="Select an option..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(field.options || {}).map(([key, optionValue]) => (
                  <SelectItem key={key} value={key}>
                    {optionValue as string}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'radio':
        console.log('[PublicForm] Radio field:', {
          fieldId: field.id,
          label: field.label,
          layout_config: field.layout_config,
          options: field.options
        });
        const radioLayout = field.layout_config?.layout || 'vertical';
        const radioLabelBelow = field.layout_config?.labelBelow || false;
        const radioEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <div>
            <RadioGroup
              value={value}
              onValueChange={(selectedValue: string) => handleInputChange(field.id, selectedValue)}
              disabled={isPreSelected}
              className={isPreSelected ? 'opacity-60' : ''}
              aria-describedby={error ? `${field.id}-error` : undefined}
            >
              <div className={radioLayout === 'horizontal'
                ? `flex flex-wrap gap-4 ${radioEvenlySpaced ? 'justify-between' : ''}`
                : 'space-y-3'}>
                {Object.entries(field.options || {}).map(([key, optionValue]) => (
                  <div
                    key={key}
                    className={`${radioLayout === 'horizontal' && radioLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${radioEvenlySpaced && radioLayout === 'horizontal' ? 'flex-1' : ''}`}
                  >
                    <RadioGroupItem value={key} id={`${field.id}-${key}`} disabled={isPreSelected} />
                    <label
                      htmlFor={`${field.id}-${key}`}
                      className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${radioLayout === 'horizontal' && radioLabelBelow ? 'mt-2 text-center' : radioLayout === 'horizontal' ? 'ml-2' : 'ml-2'}`}
                    >
                      {optionValue as string}
                    </label>
                  </div>
                ))}
              </div>
            </RadioGroup>
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'checkbox':
        console.log('[PublicForm] Checkbox field:', {
          fieldId: field.id,
          label: field.label,
          layout_config: field.layout_config,
          options: field.options
        });
        const checkboxLayout = field.layout_config?.layout || 'vertical';
        const checkboxLabelBelow = field.layout_config?.labelBelow || false;
        const checkboxEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <div>
            <div
              className={`${checkboxLayout === 'horizontal' ? `flex flex-wrap gap-4 ${checkboxEvenlySpaced ? 'justify-between' : ''}` : 'space-y-3'} ${isPreSelected ? 'opacity-60' : ''}`}
              role="group"
              aria-describedby={error ? `${field.id}-error` : undefined}
            >
              {Object.entries(field.options || {}).map(([key, optionValue]) => (
                <div
                  key={key}
                  className={`${checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${checkboxEvenlySpaced && checkboxLayout === 'horizontal' ? 'flex-1' : ''}`}
                >
                  <Checkbox
                    id={`${field.id}-${key}`}
                    checked={Array.isArray(value) ? value.includes(key) : false}
                    onCheckedChange={(checked: boolean) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      if (checked) {
                        handleInputChange(field.id, [...currentValues, key]);
                      } else {
                        handleInputChange(field.id, currentValues.filter((v: string) => v !== key));
                      }
                    }}
                    disabled={isPreSelected}
                  />
                  <label
                    htmlFor={`${field.id}-${key}`}
                    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'mt-2 text-center' : checkboxLayout === 'horizontal' ? 'ml-2' : 'ml-2'}`}
                  >
                    {optionValue as string}
                  </label>
                </div>
              ))}
            </div>
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'date':
        return (
          <div>
            <CustomDatePicker
              value={value}
              onChange={(date) => handleInputChange(field.id, date)}
              error={error}
              placeholder={field.placeholder || "Pick a date"}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
          </div>
        );
      
      case 'file':
        return (
          <div>
            <CustomFileUpload
              onChange={(file) => handleFileFieldChange(field.id, file)}
              error={error}
              placeholder={field.placeholder || "Choose a file"}
              accept={field.accept}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
          </div>
        );

      case 'image_upload':
        const imageValue = value as File | string | undefined;
        const getImageSrc = () => {
          if (!imageValue) return '';
          if (typeof imageValue === 'string') return imageValue;
          if (imageValue instanceof File) return URL.createObjectURL(imageValue);
          return '';
        };

        return (
          <div>
            <div
              className={`relative border-2 border-dashed rounded-lg transition-all ${
                error ? 'border-destructive bg-destructive/5' : 'border-muted-foreground/25 hover:border-primary/50 bg-muted/5'
              } ${isPreSelected ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${
                imageValue ? 'border-solid border-border bg-card' : ''
              }`}
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileFieldChange(field.id, file);
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
                disabled={isPreSelected}
                aria-describedby={error ? `${field.id}-error` : undefined}
              />

              {!imageValue ? (
                <div className="p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              ) : (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-foreground">Preview</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInputChange(field.id, null);
                      }}
                      disabled={isPreSelected}
                      className="h-8 px-2 text-muted-foreground hover:text-destructive relative z-20"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="relative rounded-md overflow-hidden bg-muted/20">
                    <img
                      src={getImageSrc()}
                      alt="Uploaded preview"
                      className="w-full h-auto max-h-96 object-contain"
                    />
                  </div>
                  {imageValue instanceof File && (() => {
                    const fileValue = imageValue as File;
                    return (
                      <div className="mt-3 flex items-center text-xs text-muted-foreground">
                        <FileIcon className="w-4 h-4 mr-2" />
                        <span className="truncate">{fileValue.name}</span>
                        <span className="ml-auto pl-2 flex-shrink-0">
                          {(fileValue.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'multi_row':
        return (
          <div>
            <MultiRowField
              field={field}
              value={Array.isArray(value) ? value : []}
              onChange={(multiRowValue) => handleInputChange(field.id, multiRowValue)}
              error={error}
              errors={errors}
              disabled={isPreSelected}
            />
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      case 'image':
        const getImageSizeClass = (size: string) => {
          switch (size) {
            case 'small': return 'max-w-[150px]';
            case 'medium': return 'max-w-[300px]';
            case 'large': return 'max-w-[500px]';
            case 'full': return 'w-full';
            default: return 'max-w-[300px]';
          }
        };
        
        const getImageMaxHeight = (size: string) => {
          switch (size) {
            case 'small': return '150px';
            case 'medium': return '300px';
            case 'large': return '400px';
            case 'full': return '500px';
            default: return '400px';
          }
        };

        if (field.options?.layout === 'side-by-side') {
          return (
            <div className="flex justify-between items-start w-full">
              {(field.options?.imageUrl || field.options?.imageFile) && (
                <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-start`}>
                  <img
                    src={field.options.imageFile || field.options.imageUrl}
                    alt={field.options?.imageAlt || 'Left image'}
                    className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                    style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {(field.options?.imageUrl2 || field.options?.imageFile2) && (
                <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-end`}>
                  <img
                    src={field.options.imageFile2 || field.options.imageUrl2}
                    alt={field.options?.imageAlt2 || 'Right image'}
                    className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                    style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          );
        }

        return (field.options?.imageUrl || field.options?.imageFile) ? (
          <div className={getImageSizeClass(field.options?.imageSize || 'medium')}>
            <img
              src={field.options.imageFile || field.options.imageUrl}
              alt={field.options?.imageAlt || 'Form image'}
              className={`w-full h-auto rounded ${field.options?.showBorder !== false ? 'border' : ''}`}
              style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>
        ) : null;
      
      case 'header':
        return (
          <HeaderField
            field={field}
          />
        );
      
      case 'logic':
        return <LogicField field={field} formData={formData} formFields={form?.fields || []} />;
      
      case 'av_check':
        return (
          <AVCheckField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
          />
        );

      case 'connection_check':
        return (
          <ConnectionCheckField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
          />
        );

      case 'scheduler': {
        const ready = shouldShowSchedulerField(field, formData, form?.fields || []);
        if (!ready) {
          return (
            <div className="rounded-xl border border-dashed border-muted-foreground/40 bg-muted/20 p-6 text-center">
              <p className="text-sm font-medium text-foreground">Scheduling locked</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Complete all the questions above — including the camera/mic and
                connection checks — to unlock interview scheduling.
              </p>
            </div>
          );
        }
        return (
          <SchedulerField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
            prefillName={schedulerPrefill.name}
            prefillEmail={schedulerPrefill.email}
          />
        );
      }

      case 'checkbox_with_input':
        return (
          <div>
            <div
              className={`space-y-3 ${isPreSelected ? 'opacity-60' : ''}`}
              role="group"
              aria-describedby={error ? `${field.id}-error` : undefined}
            >
              {Object.entries(field.options || {}).filter(([key]) => key !== 'inputType' && key !== 'inputPlaceholder').map(([key, optionValue]) => (
                <div key={key} className="flex items-center space-x-3">
                  <Checkbox
                    id={`${field.id}-${key}`}
                    checked={value?.[key]?.checked || false}
                    onCheckedChange={(checked: boolean) => {
                      const currentValues = value || {};
                      handleInputChange(field.id, {
                        ...currentValues,
                        [key]: {
                          checked,
                          value: currentValues[key]?.value || ''
                        }
                      });
                    }}
                    disabled={isPreSelected}
                  />
                  <label 
                    htmlFor={`${field.id}-${key}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 min-w-0"
                  >
                    {optionValue as string}
                  </label>
                  {(value?.[key]?.checked) && (
                    <Input
                      type={field.options?.inputType || 'text'}
                      placeholder={field.options?.inputPlaceholder || 'Enter value'}
                      value={value?.[key]?.value || ''}
                      onChange={(e) => {
                        const currentValues = value || {};
                        handleInputChange(field.id, {
                          ...currentValues,
                          [key]: {
                            checked: currentValues[key]?.checked || false,
                            value: e.target.value
                          }
                        });
                      }}
                      className={`flex-1 ${error ? 'border-destructive' : ''} ${isPreSelected ? 'bg-muted/50' : ''}`}
                      disabled={isPreSelected}
                    />
                  )}
                </div>
              ))}
            </div>
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      
      
      default:
        return (
          <div>
            <Input
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`${error ? 'border-destructive' : ''} ${isPreSelected ? 'bg-muted/50' : ''}`}
              disabled={isPreSelected}
              aria-describedby={error ? `${field.id}-error` : undefined}
            />
            {error && (
              <p id={`${field.id}-error`} className="text-destructive text-sm mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed top-4 right-4 z-10">
          <div className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <span className="text-sm font-medium text-foreground">{t('common.theme', { defaultValue: 'Theme' })}</span>
            <ThemeToggle />
          </div>
        </div>
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" aria-hidden="true"></div>
          <div className="sr-only" role="status" aria-live="polite">
            {t('publicForm.loadingForm')}
          </div>
          <p className="text-sm text-muted-foreground" aria-hidden="true">{t('publicForm.loadingForm')}</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed top-4 right-4 z-10">
          <div className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <span className="text-sm font-medium text-foreground">{t('common.theme', { defaultValue: 'Theme' })}</span>
            <ThemeToggle />
          </div>
        </div>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('publicForm.formNotFound', { defaultValue: 'Form Not Found' })}</h2>
            <p className="text-muted-foreground">{t('publicForm.formNotFoundDescription', { defaultValue: "The form you're looking for doesn't exist or has been unpublished." })}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed top-4 right-4 z-10">
          <div className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <span className="text-sm font-medium text-foreground">{t('common.theme', { defaultValue: 'Theme' })}</span>
            <ThemeToggle />
          </div>
        </div>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('publicForm.thankYou')}</h2>
            <p className="text-muted-foreground">{t('publicForm.submittedSuccessfully')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed top-4 right-4 z-10">
          <div className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <span className="text-sm font-medium text-foreground">{t('common.theme', { defaultValue: 'Theme' })}</span>
            <ThemeToggle />
          </div>
        </div>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('publicForm.accessRestricted', { defaultValue: 'Access Restricted' })}</h2>
            <p className="text-muted-foreground mb-4">
              {t('publicForm.accessRestrictedDescription', { defaultValue: 'This form is only accessible via specific invitation links.' })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('publicForm.correctLinkMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="fixed top-4 right-4 z-10">
          <div className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <span className="text-sm font-medium text-foreground">{t('common.theme', { defaultValue: 'Theme' })}</span>
            <ThemeToggle />
          </div>
        </div>
        
        <Card className="notion-scale-in">
          <CardHeader>
            {renderAboveTitleImages()}
            <CardTitle className="text-4xl font-bold leading-tight tracking-tight">{form.title}</CardTitle>
            {form.description && (
              <p className="text-muted-foreground mt-2">{form.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <p className="text-red-700 text-sm">{errors.general}</p>
                </div>
              )}
              {form.fields
                .filter(field => field.field_type !== 'logic' && !(field.field_type === 'image' && field.options && isImageOptions(field.options) && field.options.aboveTitle === true)) // Hide logic fields and above-title images
                .map((field) => {
                const isPreSelected = isFieldPreSelected(field.id);
                return (
                  <div key={field.id} className="space-y-3 notion-fade-in">
                    {field.field_type === 'header' || field.field_type === 'image' ? (
                      renderField(field)
                    ) : (
                      <>
                        <label className="label-medium text-foreground">
                          {field.label}
                          {field.is_required && <span className="text-red-500 ml-1">*</span>}
                          {isPreSelected && (
                            <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                              {t('publicForm.preFilled', { defaultValue: 'Pre-filled' })}
                            </span>
                          )}
                        </label>
                        {renderField(field)}
                      </>
                    )}
                  </div>
                );
              })}
              
              {!hideSubmitButton && (
                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting ? t('publicForm.submitting') : t('publicForm.submitForm')}
                  </Button>
                </div>
              )}
              {hideSubmitButton && submitting && (
                <div className="pt-4 text-center text-sm text-muted-foreground">
                  {t('publicForm.submitting')}
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicForm;